/**
 * Where the sun is (Sprint 084.0, ADR-0091; DEC-2).
 *
 * NOAA's solar-position equations — the ones behind its Solar Calculator and
 * spreadsheets, after Meeus, *Astronomical Algorithms* — written here rather
 * than taken as a dependency. About 0.01° over 1800–2100, which is more than a
 * design tool can show and less than NREL's SPA, whose extra precision nobody
 * could see. The tests hold it to JPL Horizons, an independent source, not only
 * to NOAA's own numbers: a copy of NOAA checked against NOAA proves the copy.
 *
 * Angles in degrees. Azimuth clockwise from **true** north; altitude above the
 * horizon, refraction-corrected, negative below it.
 */
const RADIANS = Math.PI / 180;
const DEGREES = 180 / Math.PI;
const MINUTE = 60_000;
/**
 * Sunrise and sunset are when the sun's centre is 0.833° below the horizon:
 * 34′ of refraction plus a 16′ semi-diameter, the upper limb just showing.
 */
export const HORIZON_DEGREES = -0.833;
function sunCoordinates(instant) {
    const t = julianCenturies(instant);
    const meanLongitude = normalise(280.46646 + t * (36000.76983 + t * 0.0003032));
    const meanAnomaly = 357.52911 + t * (35999.05029 - 0.0001537 * t);
    const eccentricity = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
    const centre = Math.sin(meanAnomaly * RADIANS) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
        Math.sin(2 * meanAnomaly * RADIANS) * (0.019993 - 0.000101 * t) +
        Math.sin(3 * meanAnomaly * RADIANS) * 0.000289;
    const omega = 125.04 - 1934.136 * t;
    const apparentLongitude = meanLongitude + centre - 0.00569 - 0.00478 * Math.sin(omega * RADIANS);
    const meanObliquity = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
    const obliquity = meanObliquity + 0.00256 * Math.cos(omega * RADIANS);
    const declination = Math.asin(Math.sin(obliquity * RADIANS) * Math.sin(apparentLongitude * RADIANS)) * DEGREES;
    const y = Math.tan((obliquity / 2) * RADIANS) ** 2;
    const l0 = meanLongitude * RADIANS;
    const m = meanAnomaly * RADIANS;
    const equationOfTime = 4 *
        DEGREES *
        (y * Math.sin(2 * l0) -
            2 * eccentricity * Math.sin(m) +
            4 * eccentricity * y * Math.sin(m) * Math.cos(2 * l0) -
            0.5 * y * y * Math.sin(4 * l0) -
            1.25 * eccentricity * eccentricity * Math.sin(2 * m));
    return {
        declinationDegrees: declination,
        equationOfTimeMinutes: equationOfTime,
        apparentLongitudeDegrees: normalise(apparentLongitude)
    };
}
export function solarGeometry(instant) {
    const { declinationDegrees, equationOfTimeMinutes } = sunCoordinates(instant);
    return { declinationDegrees, equationOfTimeMinutes };
}
/** The sun's apparent longitude, for finding solstices and equinoxes. */
export function apparentLongitude(instant) {
    return sunCoordinates(instant).apparentLongitudeDegrees;
}
export function sunPosition(instant, latitude, longitude) {
    const { declinationDegrees, equationOfTimeMinutes } = solarGeometry(instant);
    const minutesOfDay = (((instant / MINUTE) % 1440) + 1440) % 1440;
    const trueSolarTime = (((minutesOfDay + equationOfTimeMinutes + 4 * longitude) % 1440) + 1440) % 1440;
    // Degrees from local solar noon: negative before it, positive after.
    const hourAngle = trueSolarTime / 4 - 180;
    const lat = latitude * RADIANS;
    const dec = declinationDegrees * RADIANS;
    const cosZenith = clamp(Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(hourAngle * RADIANS), -1, 1);
    const zenith = Math.acos(cosZenith) * DEGREES;
    const geometricAltitude = 90 - zenith;
    const azimuth = azimuthOf(lat, dec, zenith, hourAngle);
    return {
        azimuthDegrees: azimuth,
        altitudeDegrees: geometricAltitude + refraction(geometricAltitude),
        aboveHorizon: geometricAltitude > HORIZON_DEGREES
    };
}
/** NOAA's refraction approximation, in degrees, for a geometric altitude. */
function refraction(altitude) {
    if (altitude > 85) {
        return 0;
    }
    const tangent = Math.tan(altitude * RADIANS);
    let arcSeconds;
    if (altitude > 5) {
        arcSeconds = 58.1 / tangent - 0.07 / tangent ** 3 + 0.000086 / tangent ** 5;
    }
    else if (altitude > -0.575) {
        arcSeconds =
            1735 + altitude * (-518.2 + altitude * (103.4 + altitude * (-12.79 + altitude * 0.711)));
    }
    else {
        arcSeconds = -20.772 / tangent;
    }
    return arcSeconds / 3600;
}
function azimuthOf(lat, dec, zenithDegrees, hourAngle) {
    const zenith = zenithDegrees * RADIANS;
    const denominator = Math.cos(lat) * Math.sin(zenith);
    // At a pole, or with the sun overhead, azimuth is undefined; south is as good
    // an answer as any and keeps the value finite.
    if (Math.abs(denominator) < 1e-12) {
        return 180;
    }
    const cosAzimuth = clamp((Math.sin(lat) * Math.cos(zenith) - Math.sin(dec)) / denominator, -1, 1);
    const angle = Math.acos(cosAzimuth) * DEGREES;
    return hourAngle > 0 ? normalise(angle + 180) : normalise(540 - angle);
}
function julianCenturies(instant) {
    const julianDay = instant / 86_400_000 + 2440587.5;
    return (julianDay - 2451545) / 36525;
}
function normalise(degrees) {
    return ((degrees % 360) + 360) % 360;
}
function clamp(value, low, high) {
    return Math.min(high, Math.max(low, value));
}
//# sourceMappingURL=position.js.map